import React from 'react';

export type ImageFit = 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';

export type ImagePosition = 
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

interface ImageProps {
  src: string;
  alt: string;
  fit?: ImageFit;
  position?: ImagePosition;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  onClick?: () => void;
  children?: React.ReactNode;
}

export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  fit = 'cover',
  position = 'center',
  className = '',
  style = {},
  loading = 'lazy',
  onClick,
  children,
}) => {
  const positionMap: Record<ImagePosition, string> = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center',
    'top-left': 'left top',
    'top-right': 'right top',
    'bottom-left': 'left bottom',
    'bottom-right': 'right bottom',
  };

  const imageStyle: React.CSSProperties = {
    backgroundImage: `url(${src})`,
    backgroundSize: fit,
    backgroundPosition: positionMap[position],
    backgroundRepeat: 'no-repeat',
    ...style,
  };

  // Preload the image if not lazy loading
  React.useEffect(() => {
    if (loading === 'eager') {
      const img = new window.Image();
      img.src = src;
    }
  }, [src, loading]);

  return (
    <div
      className={className}
      style={imageStyle}
      role="img"
      aria-label={alt}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export default Image;

